import { useEffect, useRef, useCallback, useState, type ReactNode } from 'react'
import maplibregl, { Map as MapLibreMap } from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { MapContext } from '../../hooks/useMap'
import { AU_BBOX } from '../../constants/auStates'
import { useContactStore } from '../../store/contactStore'

const STYLE = import.meta.env.VITE_MAPLIBRE_STYLE ?? 'https://demotiles.maplibre.org/style.json'

interface ContactMapProps {
  children?: ReactNode
  className?: string
}

export default function ContactMap({ children, className = '' }: ContactMapProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<MapLibreMap | null>(null)
  const initializedRef = useRef(false)
  const [mapReady, setMapReady] = useState(false)

  useEffect(() => {
    if (!containerRef.current || initializedRef.current) return
    initializedRef.current = true

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: STYLE,
      bounds: AU_BBOX,
      fitBoundsOptions: { padding: 20 },
      attributionControl: { compact: false },
    })

    map.addControl(new maplibregl.NavigationControl(), 'top-right')
    map.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-right')
    map.once('style.load', () => setMapReady(true))

    mapRef.current = map

    return () => {
      map.remove()
      mapRef.current = null
      initializedRef.current = false
    }
  }, [])

  const flyTo = useCallback((lon: number, lat: number, zoom = 10) => {
    mapRef.current?.flyTo({ center: [lon, lat], zoom, duration: 1200 })
  }, [])

  const fitBounds = useCallback((bbox: [number, number, number, number], padding = 60) => {
    mapRef.current?.fitBounds(bbox, { padding, duration: 1200, maxZoom: 16 })
  }, [])

  const registerFlyTo = useContactStore((s) => s.registerFlyTo)
  const registerFitBounds = useContactStore((s) => s.registerFitBounds)
  useEffect(() => { registerFlyTo(flyTo) }, [flyTo, registerFlyTo])
  useEffect(() => { registerFitBounds(fitBounds) }, [fitBounds, registerFitBounds])

  return (
    <MapContext.Provider value={{ mapRef, flyTo, mapReady }}>
      <div ref={containerRef} className={className}>
        {children}
      </div>
    </MapContext.Provider>
  )
}
